import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getTenant, updateTenant, suspendTenant, reactivateTenant, upgradePlan, deleteTenant, getOrderStats, clearCashOrders, clearCardOrders } from '../services/adminApi';

const TenantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [orderStats, setOrderStats] = useState({ cash_orders_count: 0, card_orders_count: 0 });
  const [clearingOrders, setClearingOrders] = useState(null);

  useEffect(() => {
    loadTenant();
  }, [id]);

  const loadTenant = async () => {
    try {
      const response = await getTenant(id);
      const tenantData = response.data.tenant || response.data;
      setTenant(tenantData);
      setFormData({
        name: tenantData.name,
        email: tenantData.email,
        phone: tenantData.phone || '',
      });
      // Load order stats
      loadOrderStats();
    } catch (err) {
      console.error('Failed to load tenant:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOrderStats = async () => {
    try {
      const response = await getOrderStats(id);
      setOrderStats(response.data);
    } catch (err) {
      console.error('Failed to load order stats:', err);
    }
  };

  const handleClearCashOrders = async () => {
    if (!window.confirm(`Clear ALL ${orderStats.cash_orders_count} cash orders? This cannot be undone!`)) return;
    if (!window.confirm('Are you ABSOLUTELY sure? This will delete all cash order data!')) return;
    
    setClearingOrders('cash');
    try {
      await clearCashOrders(id);
      alert('Cash orders cleared successfully');
      loadOrderStats();
    } catch (err) {
      alert('Failed to clear cash orders: ' + (err.response?.data?.message || err.message));
    } finally {
      setClearingOrders(null);
    }
  };

  const handleClearCardOrders = async () => {
    if (!window.confirm(`Clear ALL ${orderStats.card_orders_count} card orders? This cannot be undone!`)) return;
    if (!window.confirm('Are you ABSOLUTELY sure? This will delete all card order data!')) return;
    
    setClearingOrders('card');
    try {
      await clearCardOrders(id);
      alert('Card orders cleared successfully');
      loadOrderStats();
    } catch (err) {
      alert('Failed to clear card orders: ' + (err.response?.data?.message || err.message));
    } finally {
      setClearingOrders(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTenant(id, formData);
      await loadTenant();
      setEditMode(false);
    } catch (err) {
      alert('Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async () => {
    if (!window.confirm('Suspend this tenant?')) return;
    try {
      await suspendTenant(id, 'Suspended by admin');
      loadTenant();
    } catch (err) {
      alert('Failed to suspend tenant');
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateTenant(id);
      loadTenant();
    } catch (err) {
      alert('Failed to reactivate tenant');
    }
  };

  const handleUpgradePlan = async (plan) => {
    if (!window.confirm(`Upgrade to ${plan} plan?`)) return;
    try {
      await upgradePlan(id, plan);
      loadTenant();
    } catch (err) {
      alert('Failed to upgrade plan');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('DELETE this tenant? This cannot be undone!')) return;
    if (!window.confirm('Are you ABSOLUTELY sure?')) return;
    
    try {
      await deleteTenant(id);
      navigate('/super-admin/tenants');
    } catch (err) {
      alert('Failed to delete tenant');
    }
  };

  const statusColors = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    suspended: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!tenant) {
    return <div className="text-center text-gray-500">Tenant not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/super-admin/tenants" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
            <p className="text-gray-500">{tenant.subdomain}.khabeer-tech.com</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[tenant.status]}`}>
            {tenant.status}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {tenant.status === 'active' ? (
            <button
              onClick={handleSuspend}
              className="px-4 py-2 text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
            >
              Suspend
            </button>
          ) : (
            <button
              onClick={handleReactivate}
              className="px-4 py-2 text-green-600 border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
            >
              Reactivate
            </button>
          )}
          <button
            onClick={handleDelete}
            className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenant Info */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Tenant Information</h2>
            {editMode ? (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditMode(false)}
                  className="px-3 py-1.5 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditMode(true)}
                className="px-3 py-1.5 text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
              >
                Edit
              </button>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Name</label>
              {editMode ? (
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              ) : (
                <p className="text-gray-900">{tenant.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
              {editMode ? (
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              ) : (
                <p className="text-gray-900">{tenant.email}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Subdomain</label>
              <p className="text-gray-900">{tenant.subdomain}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">UUID</label>
              <p className="text-gray-500 text-sm font-mono">{tenant.uuid}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Created</label>
              <p className="text-gray-900">{new Date(tenant.created_at).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Subscription */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Subscription</h2>
          
          {tenant.subscription ? (
            <div className="space-y-4">
              <div className="p-4 bg-indigo-50 rounded-lg">
                <p className="text-sm text-indigo-600 font-medium">Current Plan</p>
                <p className="text-2xl font-bold text-indigo-900 capitalize">{tenant.subscription.plan}</p>
                <p className="text-sm text-indigo-600">{tenant.subscription.price} SAR/month</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-medium capitalize">{tenant.subscription.status}</p>
              </div>
              
              {tenant.subscription.expires_at && (
                <div>
                  <p className="text-sm text-gray-500">Expires</p>
                  <p className="font-medium">{new Date(tenant.subscription.expires_at).toLocaleDateString()}</p>
                </div>
              )}

              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm font-medium text-gray-700 mb-3">Upgrade Plan</p>
                <div className="space-y-2">
                  {['basic', 'standard', 'premium', 'enterprise'].map((plan) => (
                    <button
                      key={plan}
                      onClick={() => handleUpgradePlan(plan)}
                      disabled={tenant.subscription.plan === plan}
                      className={`w-full px-3 py-2 text-sm rounded-lg transition-colors ${
                        tenant.subscription.plan === plan
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {plan.charAt(0).toUpperCase() + plan.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No subscription</p>
          )}
        </div>
      </div>

      {/* Order Management */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Cash Orders */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-gray-900">Cash Orders</h3>
                <p className="text-2xl font-bold text-gray-700">{orderStats.cash_orders_count}</p>
              </div>
              <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <button
              onClick={handleClearCashOrders}
              disabled={clearingOrders === 'cash' || orderStats.cash_orders_count === 0}
              className="w-full px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {clearingOrders === 'cash' ? 'Clearing...' : 'Clear All Cash Orders'}
            </button>
          </div>

          {/* Card Orders */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-gray-900">Card Orders</h3>
                <p className="text-2xl font-bold text-gray-700">{orderStats.card_orders_count}</p>
              </div>
              <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <button
              onClick={handleClearCardOrders}
              disabled={clearingOrders === 'card' || orderStats.card_orders_count === 0}
              className="w-full px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {clearingOrders === 'card' ? 'Clearing...' : 'Clear All Card Orders'}
            </button>
          </div>
        </div>
      </div>

      {/* Databases */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Databases</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tenant.databases?.map((db) => (
            <div key={db.id} className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 capitalize">{db.type}</p>
              <p className="font-mono text-sm text-gray-900 truncate">{db.database_name}</p>
            </div>
          )) || <p className="text-gray-500">No databases configured</p>}
        </div>
      </div>
    </div>
  );
};

export default TenantDetail;
